import React from 'react'

// @src imports
import { EditOrderCardRoute } from 'src/orders/routes/EditOrderCardRoute'
import { ReplaceOrderCardRoute } from 'src/orders/routes/ReplaceOrderCardRoute'
import { AddCardToGiftRoute } from 'src/orders/routes/AddCardToGiftRoute'
import { AddOrderCardRoute } from 'src/orders/routes/AddOrderCardRoute'

import { CreateGroup } from 'src/contacts/components'
import {
  Alert,
  Button,
  Confirm,
  FullWindowModal,
  Icon,
  Transition,
} from 'src/chrome'
import ImportManager from 'src/import-contacts'
import Pricing from 'src/marketing/plans/containers/Pricing'
import DrawerTopControls from 'src/editor/components/DrawerTopControls'

import { ContactFragment } from 'src/graphql'

import { PaymentModal } from 'src/payments/components'

// relative imports
import {
  AddCard,
  DetailDrawer,
  EditCard,
  OrderDetail,
  OrderFooter,
  OrderHeader,
  SaveAsModal,
} from 'src/orders/components'
import { drawerState } from '../drawerState'

import drawerStyles from 'src/editor/styles/components/Drawer.module.scss'
import styles from './orders.module.scss'
import { Props } from '../types'
import useApi from '../api'
// import { Button as QDSButton } from '@sendoutcards/quantum-design-ui'
import { useSelector } from 'src/hooks'

const toggle = (prevState: boolean): boolean => !prevState

const Orders: React.FC<Props> = props => {
  const { route } = props
  const { subroute } = route
  const api = useApi(props)
  const { order, actions, orders } = api
  const { isMobile, width } = useSelector(state => state.window)
  const shouldShowSendCardBottomButton = isMobile || width <= 756

  switch (subroute?.path) {
    case AddCardToGiftRoute.path:
    case ReplaceOrderCardRoute.path:
    case AddOrderCardRoute.path: {
      if (!order) return <Transition message={'Loading your order...'} />
      return (
        <AddCard
          isLoading={orders.isLoading}
          onReload={api.reloadOrder}
          order={order}
          subroute={subroute}
          onClose={() => actions.openOrder(order.id)}
        />
      )
    }

    case EditOrderCardRoute.path: {
      if (!order) return <Transition message={'Loading your order...'} />
      return (
        <EditCard
          isLoading={orders.isLoading}
          onReload={api.reloadOrder}
          order={order}
          subroute={subroute}
          onClose={() => actions.openOrder(order.id)}
        />
      )
    }

    case undefined: {
      return (
        <div className={styles.orderWrapper} id={'tour-orderWrapper'}>
          {api.isDeleting && <Transition message={'Deleting your order...'} />}
          {order && <OrderHeader order={order} />}
          <div className={styles.orderContents}>
            {order && route.orderId && (
              <OrderDetail
                shouldBlink={!!api.shouldBlink.returnAddress}
                order={order}
                toggleAddressBlinker={() => api.toggleAddressBlinker(null)}
                onAddCard={api.handleAddCard}
                shouldBlinkAddRecipients={api.shouldBlink.addRecipients}
                openAddressBook={api.handleOpenDrawer}
                openImportManager={() => api.setIsImportModalOpen(toggle)}
              />
            )}
          </div>
          {shouldShowSendCardBottomButton && (
            <div style={{ height: '50px', opacity: 0 }} />
          )}
          {orders.drawerState !== drawerState.closed &&
            orders.drawerState !== drawerState.detail && (
              <DetailDrawer
                className={
                  orders.drawerState === 'addressDrawer'
                    ? drawerStyles.fullDrawer
                    : false
                }
              >
                <DrawerTopControls className={styles.addresses}>
                  <div
                    className={`${styles.flexButton} ${styles.contactActions}`}
                  >
                    {api.drawerTriggerText && api.drawerTriggerIcon && (
                      <div style={{ display: 'flex' }}>
                        {api.drawerTriggerText.map((_, index) => {
                          return (
                            <Button
                              id={`add_new_address_btn`}
                              key={index}
                              className={index === 1 ? styles.hideMobile : ''}
                              title={api.drawerTriggerText[index]}
                              style={
                                index === 0
                                  ? { marginRight: 15 }
                                  : { width: 149 }
                              }
                              onClick={api.drawerActionTop[index]}
                            />
                          )
                        })}
                      </div>
                    )}
                    <span
                      style={{ marginLeft: 'auto!important' }}
                      className={styles.attachButton}
                    >
                      <Icon
                        icon={'CLOSE'}
                        color={'#6f8394'}
                        size={18}
                        onClick={api.handleCloseDrawer}
                      />
                    </span>
                  </div>
                </DrawerTopControls>
                {api.drawerContent}
              </DetailDrawer>
            )}
          {api.isDeleteModalOpen && (
            <Confirm
              title={'Cancel this order?'}
              message={'Are you sure you want to cancel this order?'}
              confirmTitle={'Yes, cancel this order'}
              declineTitle={'No'}
              onDecline={() => api.setIsDeleteModalOpen(toggle)}
              onConfirm={api.handleDelete}
            />
          )}
          {order && (
            <>
              {shouldShowSendCardBottomButton && (
                <div className={styles.sendButton}>
                  {/* <QDSButton
                    title="Send Card"
                    type="primary"
                    size="large"
                    fullWidth={true}
                    outlined={true}
                    onClick={api.handleOrderVerification}
                  /> */}
                  <Button
                    id={'checkout_btn'}
                    title={'Send Card'}
                    className={styles.cta}
                    style={{ margin: 'auto' }}
                    onClick={api.handleOrderVerification}
                    buttonColor={'pink'}
                  />
                </div>
              )}
              <OrderFooter
                isSaving={orders.isSaving}
                addRecipients={api.handleOpenDrawer}
                isInvalid={
                  !order.returnAddress ||
                  !order.lines.length ||
                  !order.lines[0].card ||
                  (!order.contacts.length && !order.groups.length)
                }
                toggleAddressBlinker={api.toggleAddressBlinker}
                onSendOrder={api.handleOrderVerification}
                saveAs={() => api.setIsSaveAsModalOpen(toggle)}
                cancel={() => api.setIsDeleteModalOpen(toggle)}
                order={order}
              />
            </>
          )}
          <CreateGroup
            isOpen={orders.isGroupModalOpen}
            onClose={actions.closeGroupModal}
            onSubmit={api.createGroup}
            contacts={orders.groupMembers as ContactFragment[]}
          />
          {orders.uiContext === 'CONFIRM_BLANK_PANELS' && (
            <Confirm
              title={'Blank Card Panels'}
              message={`It looks like you may have empty panels on your card.
                  Click Confirm to continue anyway, or Cancel to take one last look before you send it out`}
              confirmTitle={'Confirm'}
              onDecline={() => actions.setOrderUiContext(null)}
              onConfirm={() => {
                actions.setOrderUiContext(null)
                api.handleOrderSend()
              }}
            />
          )}
          {!!orders.freeStatusChanged && (
            <Alert
              isOpen={!!orders.freeStatusChanged}
              title={'Free Send Status Changed'}
              message={
                orders.freeStatusChanged
                  ? orders.freeStatusChanged.join(' ')
                  : 'There was a change'
              }
              type={'info'}
              onClose={actions.changeFreeStatus}
              timeout={12000}
            />
          )}
          {api.isImportAlertOpen && (
            <Alert
              isOpen={api.isImportAlertOpen}
              title={'This import is limited to 100 contacts'}
              message={[
                'You are currently on a limited subscription.',
                'In order to access the full functionality of the relationship manager,',
                'please upgrade to a Premium or Enterprise plan.',
              ].join(' ')}
              onClose={api.closeImportAlert}
              type={'info'}
            >
              <span
                onClick={() => {
                  api.closeImportAlert()
                  api.setIsUpsaleModalOpen(toggle)
                }}
              >
                Click here to view your plan options
              </span>
            </Alert>
          )}
          {order && api.isSaveAsModalOpen && (
            <SaveAsModal
              existingCampaign={order.campaign}
              lines={order.lines}
              recipientIds={api.recipientIds}
              onClose={() => api.setIsSaveAsModalOpen(toggle)}
              onSubmit={api.handleSubmit}
            />
          )}
          <PaymentModal
            isOpen={api.isPaymentModalOpen}
            onSuccess={() => api.setIsPaidSuccessfully(true)}
            paymentInfo={api.paymentInfo}
            onClose={() => {
              api.setIsPaymentModalOpen(false)
              api.setPaymentInfo(undefined)
              if (api.isPaidSuccessfully) {
                window.analytics.track('Send Order')
                actions.clearOrder()
                actions.openCatalog()
              }
            }}
          />
          {api.isUpsaleModalOpen && (
            <FullWindowModal
              style={{ zIndex: 505 }}
              closeStyle={{ zIndex: 506 }}
              closeIcon={'ARROWLEFT'}
              closeMessage={'Back to Import'}
              close={() => api.setIsUpsaleModalOpen(toggle)}
            >
              <Pricing />
            </FullWindowModal>
          )}
          {api.isImportModalOpen && (
            <FullWindowModal
              style={{ zIndex: 505 }}
              closeStyle={{ zIndex: 506 }}
              close={() => api.setIsImportModalOpen(toggle)}
              closeIcon={'ARROWLEFT'}
              closeMessage={'To Order'}
            >
              <ImportManager
                additionalAction={{
                  onClick: api.handleImportContacts,
                  title: 'Attach to order',
                }}
                rowLimit={undefined}
              />
            </FullWindowModal>
          )}
        </div>
      )
    }
  }
}

export default Orders
